/**
 * Standard error response structure
 */
export interface TypeErrorResponse {
  ok: boolean
  status: number
  error: string
  errorCode?: string
  errors?: GraphQLErrorDetail[]
}

/**
 * GraphQL error detail structure
 */
export interface GraphQLErrorDetail {
  message: string
  extensions?: {
    code?: string
		identifier?: string
    status?: number
    http?: {
      status?: number
    }
    [key: string]: unknown
  }
}

/**
 * Apollo/GraphQL error structure
 */
export interface ApolloError {
  graphQLErrors?: GraphQLErrorDetail[]
  networkError?: {
    statusCode?: number
    message?: string
  } | null
  message?: string
}

/**
 * Extract HTTP status code from GraphQL error extensions
 */
const extractStatusFromError = (error: GraphQLErrorDetail): number => {
  // Try multiple common patterns for status code
  const statusFromExtensions =
    error.extensions?.http?.status ||
    error.extensions?.status ||
    undefined

  if (statusFromExtensions) return statusFromExtensions

  // Fallback: analyze error message for common patterns
  const message = error.message?.toLowerCase() || ''
  if (message.includes('unauthorized') || message.includes('invalid email or password')) return 401
  if (message.includes('forbidden')) return 403
  if (message.includes('not found')) return 404

  return 500
}

export const catchError = (error: unknown):TypeErrorResponse => {
	const err = error as ApolloError
	// console.log('🟣 catchError | error:', err)
  // Handle ApolloError with graphQLErrors
  if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
		const firstError = err.graphQLErrors[0]
		console.log('🟣 err.graphQLErrors[0]:', err.graphQLErrors)
    const status = extractStatusFromError(firstError)

    return {
      ok: false,
      status,
      error: firstError.message || 'An error occurred',
      errorCode: firstError.extensions?.code,
      errors: err.graphQLErrors
    }
  }

  // Handle network errors
  if (err?.networkError) {
    return {
      ok: false,
      status: err.networkError.statusCode || 503,
      error: err.networkError.message || 'Network error occurred',
      errorCode: 'NETWORK_ERROR'
    }
  }

  // Handle generic errors
  const genericError = error as Error
  return {
    ok: false,
    status: 500,
    error: genericError?.message || 'An unexpected error occurred',
    errorCode: 'UNKNOWN_ERROR'
  }
}

export const getErrorMessage = (error: TypeErrorResponse, t): string => {
  const graphQLCode = error.errors?.[0]?.extensions?.code || ''
  const graphQLIdentifier = error.errors?.[0]?.extensions?.identifier || ''
  const customMessageByCode = MESSAGE_KEYS_BY_CODE[graphQLCode]

  if(customMessageByCode) return `${graphQLIdentifier ? `<span>${graphQLIdentifier}</span>` : ''}${t(customMessageByCode) as string}`
  else if(typeof error === 'string') return error
  else return 'UNHANDLED_ERROR'
}

const MESSAGE_KEYS_BY_CODE = {
  // // CustomerLogin
	CUSTOMER_INVALID_CREDENTIALS: 'form.errors.login_failed_invalid_email_or_password',
  CMS_CUSTOMER_NOT_FOUND: 'form.errors.login_failed_invalid_email_or_password',
  BIGCOMMERCE_SESSION_UPDATE_FAILED: 'form.errors.login_not_available_in_this_moment',
  // // GenerateToken
  // GENERATE_TOKEN
  // // ActivateCustomerAccount
  INVITE_TOKEN_INVALID: 'form.errors.invite_token_invalid_or_expired',
  INVITE_TOKEN_EXPIRED: 'form.errors.invite_token_invalid_or_expired',
  // CUSTOMER_NOT_FOUND
  // // ResetCustomerPassword
  RESET_PASSWORD_TOKEN_INVALID: 'form.errors.token_invalid_or_expired',
  RESET_PASSWORD_TOKEN_EXPIRED: 'form.errors.token_invalid_or_expired',
  // BIGCOMMERCE_PASSWORD_NOT_UPDATED
  // // SendCustomerInviteEmail
  // CUSTOMER_INVITE_EMAIL
  // // B2C Register
  BIGCOMMERCE_CUSTOMER_NOT_CREATED: 'form.errors.signup_failed',
  // CMS_CUSTOMER_NOT_UPDATED
  // // For all queries and mutations
  // WEBSITE_NOT_FOUND
  // // DY-Access-Token
  // ACCESS_TOKEN_INVALID
  // ACCESS_TOKEN_EXPIRED
  // // ACCESS_TOKEN_MISSING
  // // DY-Customer-Token
	// CUSTOMER_TOKEN_REQUIRED
  // CUSTOMER_LOGIN_TOKEN_INVALID
  // CUSTOMER_LOGIN_TOKEN_EXPIRED
	// CUSTOMER_INVALID_CREDENTIALS
  // // Validations
  // // CMS_VALIDATION + attribute
	// // Cart
	CART_NOT_FOUND: 'project.error_codes_messages.CART_NOT_FOUND',
	CART_LINE_QUANTITY_INVALID: 'project.error_codes_messages.CART_LINE_QUANTITY_INVALID',
	CART_LINE_QUANTITY_INVALID_FOR_MIN_SALE_UNITS: 'project.error_codes_messages.CART_LINE_QUANTITY_INVALID_FOR_MIN_SALE_UNITS',
	PRODUCT_NOT_FOUND: 'project.error_codes_messages.PRODUCT_NOT_FOUND',
	WEBSITE_PRODUCT_NOT_FOUND: 'project.error_codes_messages.WEBSITE_PRODUCT_NOT_FOUND',
	VARIANT_NOT_FOUND: 'project.error_codes_messages.VARIANT_NOT_FOUND',
	WEBSITE_VARIANT_NOT_FOUND: 'project.error_codes_messages.WEBSITE_VARIANT_NOT_FOUND',
	VARIANT_OUT_OF_STOCK: 'project.error_codes_messages.VARIANT_OUT_OF_STOCK',
	VARIANT_INSUFFICIENT_STOCK: 'project.error_codes_messages.VARIANT_INSUFFICIENT_STOCK',
	PRODUCT_ALREADY_IN_STOCK_ERROR: 'project.error_codes_messages.PRODUCT_ALREADY_IN_STOCK_ERROR',
	ERROR_CREATING_CART_LINE: 'project.error_codes_messages.ERROR_CREATING_CART_LINE',
	CART_LINES_COMBINATION: 'project.error_codes_messages.CART_LINES_COMBINATION',
	CART_LINES_NOT_FROZEN: 'project.error_codes_messages.CART_LINES_NOT_FROZEN',
	CART_LINES_NOT_FRESH: 'project.error_codes_messages.CART_LINES_NOT_FRESH',
	CART_SMALLER_THAN_MINIMUM_PURCHASE: 'project.error_codes_messages.CART_SMALLER_THAN_MINIMUM_PURCHASE',
	CART_NO_DELIVERY_DATES: 'project.error_codes_messages.CART_NO_DELIVERY_DATES',
	CART_DELIVERY_DATE_INVALID: 'project.error_codes_messages.CART_DELIVERY_DATE_INVALID',
	CUSTOMER_NOT_FOUND: 'project.error_codes_messages.CUSTOMER_NOT_FOUND',
	CUSTOMER_NO_ADDRESS: 'project.error_codes_messages.CUSTOMER_NO_ADDRESS',
	NO_CART_LINES: 'project.error_codes_messages.NO_CART_LINES',
	BIGCOMMERCE_ERROR_CONNECTION: 'project.error_codes_messages.BIGCOMMERCE_ERROR_CONNECTION',
	BIGCOMMERCE_ERROR_CHECK_VARIANT_STOCK: 'project.error_codes_messages.BIGCOMMERCE_ERROR_CHECK_VARIANT_STOCK',
	BIGCOMMERCE_ERROR_CART_DELETE: 'project.error_codes_messages.BIGCOMMERCE_ERROR_CART_DELETE',
	BIGCOMMERCE_ERROR_CART_CREATE: 'project.error_codes_messages.BIGCOMMERCE_ERROR_CART_CREATE',
	BIGCOMMERCE_ERROR_GET_CHECKOUT: 'project.error_codes_messages.BIGCOMMERCE_ERROR_GET_CHECKOUT',
	BIGCOMMERCE_ERROR_UPDATE_BILLING_ADDRESS: 'project.error_codes_messages.BIGCOMMERCE_ERROR_UPDATE_BILLING_ADDRESS',
	BIGCOMMERCE_ERROR_CREATE_CONSIGNMENT: 'project.error_codes_messages.BIGCOMMERCE_ERROR_CREATE_CONSIGNMENT',

	// attribute: 'customer'
	CUSTOMER_BLOCKED: 'project.error_codes_messages.CUSTOMER_BLOCKED',
}


// code: 'INVITE_TOKEN_EXPIRED',
// code: 'RESET_PASSWORD_TOKEN_INVALID',
// code: 'RESET_PASSWORD_TOKEN_EXPIRED',
// code: 'B2B_CUSTOMER_NOT_ALLOWED',
// code: 'CUSTOMER_REGISTRATION_NOT_ALLOWED',
// code: 'NO_WEBSITE_STRATEGY_FOUND',
// code: 'B2B_ADDRESS_NOT_ALLOWED',
// code: 'ADDRESS_NOT_FOUND',
// code: 'GUEST_CART_TOKEN_INVALID',
// code: 'GUEST_CART_TOKEN_EXPIRED',
// code: 'GUEST_CART_TOKEN_MISSING',
// code: 'GUEST_CART_POSTAL_CODE_REQUIRED',
// code: 'GUEST_CARTS_NOT_ENABLED',
// code: 'CANNOT_SET_GUEST_POSTAL_CODE_FOR_CUSTOMER_CART',

// code: 'BIGCOMMERCE_CUSTOMER_CREATE_ERROR',
// code: 'BIGCOMMERCE_CUSTOMER_UPDATE_ERROR',
// code: 'BIGCOMMERCE_ADDRESS_CREATE_ERROR',
// code: 'BIGCOMMERCE_ADDRESS_UPDATE_ERROR',
// code: 'BIGCOMMERCE_ADDRESS_DELETE_ERROR',
// code: 'BIGCOMMERCE_SESSION_UPDATE_FAILED',
// code: 'BIGCOMMERCE_PASSWORD_NOT_UPDATED',
// code: 'CMS_CUSTOMER_CREATE_ERROR',
// code: 'CMS_CUSTOMER_UPDATE_ERROR',
// code: 'CMS_ADDRESS_CREATE_ERROR',
// code: 'CMS_ADDRESS_UPDATE_ERROR',
// code: 'CMS_ADDRESS_DELETE_ERROR',
// code: 'VALIDATION_ERROR',
// code: 'CMS_VALIDATION_ERROR',
// code: 'GRAPHQL_MISSING_REQUIRED_ARGUMENT_ERROR',